import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Alert, Button, ErrorBoundary, Input, PageContainer, Select, Skeleton, TextArea, useToast } from '@bbpos/ui';
import { usePurchaseOrder, useAmendPO } from '@/features/purchase-orders';
import { taxRateOptions } from '@/features/purchase-orders/line-totals';
import { productService } from '@/services/product.service';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { useAuthStore } from '@/stores/use-auth-store';
import { ROLE_LEVELS } from '@/types/user';
import { GLASS_FORM, cn } from '@/constants/design-tokens';
import { docCurrencySymbol, formatDocAmount } from '@/utils/purchase-currency';
import { paiseToRupeeInput } from '@/utils/price-input';
import { buildProductSearchParams } from './purchase-order-products';
import { amendChanged, amendTotal, buildAmendPayload, rowError, rowsFromPo } from './po-amend';
import type { AmendRow } from './po-amend';
import { ArrowLeftIcon, PlusIcon, TrashIcon } from './icons';

const UNITS = ['PCS', 'KG', 'G', 'L', 'ML', 'DOZEN', 'BOX', 'CARTON'].map((u) => ({ value: u, label: u }));

/**
 * Amend an approved or partly received purchase order (POS comments item 10):
 * the supplier sends a substitute or a different quantity. Owners apply it at
 * once; a Manager's amendment that raises the total goes to an Owner.
 */
function AmendContent() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const tenantCurrency = useTenantCurrency();
  const roleLevel = ROLE_LEVELS[useAuthStore((s) => s.profile?.role) ?? ''] ?? 0;
  const isOwner = roleLevel >= ROLE_LEVELS.OWNER;
  const { data: po, isLoading } = usePurchaseOrder(id);
  const amend = useAmendPO();
  // An amendment is in the PO's own currency (docs/multi-currency-purchasing-plan.md D1).
  const currency = po?.currency_code ?? tenantCurrency;
  const symbol = docCurrencySymbol(currency);

  const original = useMemo(() => (po ? rowsFromPo(po, currency) : []), [po, currency]);
  const [rows, setRows] = useState<AmendRow[]>([]);
  const [reason, setReason] = useState('');
  const [search, setSearch] = useState('');
  useEffect(() => { setRows(original); }, [original]);

  const { data: productsData } = useQuery({
    queryKey: ['products', 'po-amend', search],
    queryFn: () => productService.list(buildProductSearchParams(search)),
    placeholderData: keepPreviousData,
  });
  const productOptions = (productsData?.data ?? []).map((p) => ({ value: p.id, label: `${p.name} (${p.sku})` }));

  if (isLoading || !po) {
    return <div className="space-y-4"><Skeleton variant="rect" height={80} /><Skeleton variant="rect" height={320} /></div>;
  }
  if (po.status !== 'ORDERED' && po.status !== 'PARTIALLY_RECEIVED') {
    return <Alert variant="warning" description={`A ${po.status.toLowerCase().replace('_', ' ')} purchase order cannot be amended.`} />;
  }

  const newTotal = amendTotal(rows, currency, tenantCurrency);
  const errors = rows.map(rowError);
  const changed = amendChanged(rows, original);
  const needsApproval = !isOwner && newTotal > po.total_amount;
  const update = (key: string, patch: Partial<AmendRow>) =>
    setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  function addRow() {
    setRows((rs) => [...rs, {
      key: `new-${Date.now()}`, name: '', received: 0, closed: false, removed: false,
      quantity: '1', unit: 'PCS', unit_cost: '', gst_rate: '0', discount_type: 'FLAT', discount_value: '',
    }]);
  }

  async function submit() {
    try {
      const res = await amend.mutateAsync({
        id: id!, payload: { lines: buildAmendPayload(rows, currency), ...(reason.trim() ? { reason: reason.trim() } : {}) },
      });
      toast({
        title: res.applied
          ? `${po!.po_number} amended — Rev ${res.revision.revision_no}. Use Notify Supplier to send the revised PO.`
          : `Rev ${res.revision.revision_no} sent to the owner for approval`,
        variant: 'success',
      });
      navigate(`/inventory/purchase-orders/${id}`);
    } catch (err) {
      toast({ title: (err as Error).message || 'Could not amend the purchase order', variant: 'error' });
    }
  }

  return (
    <div className="space-y-5 animate-fade-in">
      <div className="rounded-xl bg-gradient-to-r from-teal-600 to-brand-600 p-5 text-white shadow-lg">
        <h2 className="text-lg font-semibold">Amend {po.po_number}</h2>
        <p className="text-sm text-white/80">
          Change quantities or costs, add an item the supplier is sending instead, or remove a line nothing has arrived for.
          Received quantities stay as they are.
        </p>
      </div>

      <div className={cn(GLASS_FORM, 'overflow-x-auto p-4')}>
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-neutral-200 text-left text-[11px] uppercase tracking-wider text-neutral-500 dark:border-neutral-700">
              <th className="px-2 py-2">Item</th>
              <th className="px-2 py-2 text-right">Received</th>
              <th className="px-2 py-2 w-28">Quantity</th>
              <th className="px-2 py-2 w-28">Unit</th>
              <th className="px-2 py-2 w-32">Unit cost ({symbol})</th>
              <th className="px-2 py-2 w-28">Tax %</th>
              <th className="px-2 py-2 w-40">Discount</th>
              <th className="px-2 py-2" />
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.key} className={cn('border-b border-neutral-100 align-top even:bg-neutral-50 dark:border-neutral-800 dark:even:bg-neutral-800/40', r.removed && 'opacity-50', !r.id && 'bg-teal-50/60 dark:bg-teal-900/10')}>
                <td className="px-2 py-2">
                  {r.id ? (
                    <div className="font-medium text-neutral-900 dark:text-white">{r.name}{r.closed && <span className="ml-2 rounded-full bg-neutral-100 px-2 py-0.5 text-xs text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">Closed</span>}</div>
                  ) : (
                    <Select
                      variant="searchable"
                      options={[
                        ...(r.product_id && !productOptions.some((o) => o.value === r.product_id) ? [{ value: r.product_id, label: r.name }] : []),
                        ...productOptions,
                      ]}
                      value={r.product_id ?? ''}
                      onChange={(v) => {
                        const pid = typeof v === 'string' ? v : '';
                        const p = (productsData?.data ?? []).find((x) => x.id === pid);
                        update(r.key, { product_id: pid, name: p?.name ?? '', unit_cost: p?.cost && !po.currency_code ? paiseToRupeeInput(p.cost, tenantCurrency) : r.unit_cost });
                      }}
                      onSearch={setSearch}
                      placeholder="Select product"
                    />
                  )}
                  {errors[i] && <p className="mt-1 text-xs text-danger-600 dark:text-danger-400">{errors[i]}</p>}
                </td>
                <td className="px-2 py-2 text-right tabular-nums text-neutral-600 dark:text-neutral-300">{r.received}</td>
                <td className="px-2 py-2"><Input type="number" value={r.quantity} onChange={(v) => update(r.key, { quantity: v })} disabled={r.removed} /></td>
                <td className="px-2 py-2"><Select options={UNITS} value={r.unit} onChange={(v) => update(r.key, { unit: String(v) })} disabled={r.removed} /></td>
                <td className="px-2 py-2"><Input type="number" value={r.unit_cost} onChange={(v) => update(r.key, { unit_cost: v })} disabled={r.removed} /></td>
                <td className="px-2 py-2"><Select options={taxRateOptions(tenantCurrency, r.gst_rate)} value={r.gst_rate} onChange={(v) => update(r.key, { gst_rate: String(v) })} disabled={r.removed} /></td>
                <td className="px-2 py-2">
                  <div className="flex gap-1">
                    <Select
                      options={[{ value: 'PERCENTAGE', label: '%' }, { value: 'FLAT', label: symbol }]}
                      value={r.discount_type}
                      onChange={(v) => update(r.key, { discount_type: v === 'PERCENTAGE' ? 'PERCENTAGE' : 'FLAT' })}
                      disabled={r.removed}
                    />
                    <div className="w-20"><Input type="number" value={r.discount_value} onChange={(v) => update(r.key, { discount_value: v })} disabled={r.removed} /></div>
                  </div>
                </td>
                <td className="px-2 py-2 text-right">
                  {r.id ? (
                    r.received === 0 && (
                      <Button variant="ghost" size="sm" onClick={() => update(r.key, { removed: !r.removed })}>
                        {r.removed ? 'Undo' : <TrashIcon className="h-4 w-4" />}
                      </Button>
                    )
                  ) : (
                    <Button variant="ghost" size="sm" onClick={() => setRows((rs) => rs.filter((x) => x.key !== r.key))}><TrashIcon className="h-4 w-4" /></Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button variant="secondary" size="sm" onClick={addRow}><PlusIcon className="mr-1 h-4 w-4" />Add item</Button>
          {rows.some((r) => !r.id) && (
            <div className="w-64"><Input value={search} onChange={setSearch} placeholder="Search products to add…" /></div>
          )}
        </div>
      </div>

      <div className={cn(GLASS_FORM, 'grid gap-4 p-4 md:grid-cols-2')}>
        <div>
          <label htmlFor="amend-reason" className="mb-1 block text-sm font-medium text-neutral-700 dark:text-neutral-200">Reason</label>
          <TextArea id="amend-reason" value={reason} onChange={setReason} rows={3} placeholder="e.g. Supplier discontinued 16W and is sending 18W instead" />
        </div>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-neutral-500">Current total</span><span className="tabular-nums">{formatDocAmount(po.total_amount, currency)}</span></div>
          <div className="flex justify-between font-semibold"><span>New total</span><span className="tabular-nums">{formatDocAmount(newTotal, currency)}</span></div>
          {needsApproval && (
            <p className="rounded-lg bg-warning-50 px-3 py-2 text-xs text-warning-800 dark:bg-warning-900/20 dark:text-warning-200">
              This raises the total, so it goes to the owner for approval. The PO stays as it is until then.
            </p>
          )}
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={() => navigate(`/inventory/purchase-orders/${id}`)}>Cancel</Button>
        <Button
          variant="primary"
          onClick={() => { void submit(); }}
          disabled={!changed || errors.some(Boolean) || amend.isPending}
          data-testid="submit-amendment"
        >
          {amend.isPending ? 'Saving…' : needsApproval ? 'Send for approval' : 'Save amendment'}
        </Button>
      </div>
    </div>
  );
}

export function PurchaseOrderAmendPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  return (
    <PageContainer
      title="Amend Purchase Order"
      breadcrumbs={[{ label: 'Purchase' }, { label: 'Purchase Orders', href: '/purchase-orders' }, { label: 'Amend' }]}
      actions={<Button variant="ghost" size="sm" onClick={() => navigate(`/inventory/purchase-orders/${id}`)}><ArrowLeftIcon className="mr-1 h-4 w-4" />Back</Button>}
    >
      <ErrorBoundary>
        <AmendContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
